/**
 * Firefly Particles System
 * Lightweight, reusable particle background for Shopify sections
 * Usage: Add canvas with class "firefly-canvas" and data attributes
 */

class FireflyParticles {
  constructor(canvas) {
    this.canvas = canvas;
    this.ctx = canvas.getContext('2d', { alpha: true });
    this.particles = [];
    this.animationId = null;
    
    // Read configuration from data attributes
    this.config = {
      color: canvas.dataset.particleColor || '#ffd700',
      count: parseInt(canvas.dataset.particleCount) || 50,
      speed: parseFloat(canvas.dataset.particleSpeed) || 1,
      size: parseFloat(canvas.dataset.particleSize) || 1
    };
    
    this.rgb = this.hexToRgb(this.config.color);
    this.init();
  }
  
  hexToRgb(hex) {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? {
      r: parseInt(result[1], 16),
      g: parseInt(result[2], 16),
      b: parseInt(result[3], 16)
    } : { r: 255, g: 215, b: 0 };
  }
  
  resizeCanvas() {
    this.canvas.width = this.canvas.offsetWidth;
    this.canvas.height = this.canvas.offsetHeight;
  }
  
  init() {
    this.resizeCanvas();
    this.createParticles();
    this.animate();
    
    // Handle resize with debounce
    let resizeTimeout;
    this.resizeHandler = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        this.resizeCanvas();
        this.createParticles();
      }, 250);
    };
    window.addEventListener('resize', this.resizeHandler);
  }
  
  createParticles() {
    this.particles = [];
    for (let i = 0; i < this.config.count; i++) {
      this.particles.push(new Particle(this.canvas, this.config));
    }
  }
  
  animate() {
    this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
    
    this.particles.forEach(particle => {
      particle.update();
      particle.draw(this.ctx, this.rgb);
    });
    
    this.animationId = requestAnimationFrame(() => this.animate());
  }
  
  destroy() {
    cancelAnimationFrame(this.animationId);
    window.removeEventListener('resize', this.resizeHandler);
  }
}

class Particle {
  constructor(canvas, config) {
    this.canvas = canvas;
    this.config = config;
    this.reset();
    this.y = Math.random() * canvas.height;
    this.baseSize = (Math.random() * 2 + 1) * config.size;
    this.phase = Math.random() * Math.PI * 2;
  }
  
  reset() {
    this.x = Math.random() * this.canvas.width;
    this.y = Math.random() * this.canvas.height;
    this.vx = (Math.random() - 0.5) * 0.5 * this.config.speed;
    this.vy = (Math.random() - 0.5) * 0.5 * this.config.speed;
    this.baseSize = (Math.random() * 2 + 1) * this.config.size;
    this.phase = Math.random() * Math.PI * 2;
  }
  
  update() {
    this.x += this.vx;
    this.y += this.vy;
    this.phase += 0.02 * this.config.speed;
    
    this.vx += (Math.random() - 0.5) * 0.05;
    this.vy += (Math.random() - 0.5) * 0.05;
    this.vx *= 0.98;
    this.vy *= 0.98;
    
    if (this.x < -10) this.x = this.canvas.width + 10;
    if (this.x > this.canvas.width + 10) this.x = -10;
    if (this.y < -10) this.y = this.canvas.height + 10;
    if (this.y > this.canvas.height + 10) this.y = -10;
  }
  
  draw(ctx, rgb) {
    const pulse = Math.sin(this.phase) * 0.5 + 0.5;
    const size = this.baseSize * (0.5 + pulse * 0.5);
    const alpha = 0.3 + pulse * 0.7;
    
    const gradient = ctx.createRadialGradient(this.x, this.y, 0, this.x, this.y, size * 3);
    gradient.addColorStop(0, `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${alpha})`);
    gradient.addColorStop(0.5, `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${alpha * 0.3})`);
    gradient.addColorStop(1, `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0)`);
    
    ctx.fillStyle = gradient;
    ctx.beginPath();
    ctx.arc(this.x, this.y, size * 3, 0, Math.PI * 2);
    ctx.fill();
    
    ctx.fillStyle = `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${alpha})`;
    ctx.beginPath();
    ctx.arc(this.x, this.y, size, 0, Math.PI * 2);
    ctx.fill();
  }
}

// Auto-initialize all firefly canvases
document.addEventListener('DOMContentLoaded', () => {
  const instances = new Map();
  
  const initFireflies = () => {
    document.querySelectorAll('.firefly-canvas').forEach(canvas => {
      if (!instances.has(canvas)) {
        instances.set(canvas, new FireflyParticles(canvas));
      }
    });
  };
  
  initFireflies();
  
  // Handle Shopify theme editor
  document.addEventListener('shopify:section:load', initFireflies);
  document.addEventListener('shopify:section:unload', (event) => {
    const section = event.target;
    const canvas = section.querySelector('.firefly-canvas');
    if (canvas && instances.has(canvas)) {
      instances.get(canvas).destroy();
      instances.delete(canvas);
    }
  });
});